/**
 * SoleadLab 2.0 — State Snapshot API Client (C2.5)
 *
 * HTTP client for the state snapshot endpoints exposed by C2.3 (recompute)
 * and C2.4 (timeline + delta). All state computation is backend-owned;
 * this service is strictly a thin fetch wrapper.
 */

import type {
  SiteStateSnapshot,
  AssetStateSnapshot,
  SiteStateDelta,
  AssetStateDelta,
  RecomputeResult,
  PaginatedSnapshots,
} from '@/types/state';
import { apiFetch } from './apiFetch';

// ─── Site state endpoints (C2.4) ──────────────────────────────────────────────

/**
 * GET /api/v2/sites/:siteId/state/latest
 * Returns the most recent site_state_snapshots row, or throws 404 if none exist.
 */
export async function getSiteStateLatest(siteId: string): Promise<SiteStateSnapshot> {
  const { snapshot } = await apiFetch<{ snapshot: SiteStateSnapshot }>(
    `/api/v2/sites/${siteId}/state/latest`,
  );
  return snapshot;
}

/**
 * GET /api/v2/sites/:siteId/state/snapshots
 * Paginated timeline of site state snapshots, newest first.
 */
export async function listSiteStateSnapshots(
  siteId: string,
  page = 1,
  limit = 20,
): Promise<PaginatedSnapshots<SiteStateSnapshot>> {
  return apiFetch<PaginatedSnapshots<SiteStateSnapshot>>(
    `/api/v2/sites/${siteId}/state/snapshots?page=${page}&limit=${limit}`,
  );
}

/**
 * GET /api/v2/sites/:siteId/state/delta
 * Structured delta between two site snapshots.
 * If `toId` is omitted the backend defaults to the most recent snapshot.
 */
export async function getSiteStateDelta(
  siteId: string,
  fromId: string,
  toId?: string,
): Promise<SiteStateDelta> {
  const qs = toId
    ? `from=${encodeURIComponent(fromId)}&to=${encodeURIComponent(toId)}`
    : `from=${encodeURIComponent(fromId)}`;
  return apiFetch<SiteStateDelta>(`/api/v2/sites/${siteId}/state/delta?${qs}`);
}

// ─── Asset state endpoints (C2.4) ─────────────────────────────────────────────

/**
 * GET /api/v2/sites/:siteId/asset-nodes/:nodeId/state/latest
 * Returns the most recent asset_state_snapshots row for the node.
 */
export async function getAssetStateLatest(
  siteId: string,
  nodeId: string,
): Promise<AssetStateSnapshot> {
  const { snapshot } = await apiFetch<{ snapshot: AssetStateSnapshot }>(
    `/api/v2/sites/${siteId}/asset-nodes/${nodeId}/state/latest`,
  );
  return snapshot;
}

/**
 * GET /api/v2/sites/:siteId/asset-nodes/:nodeId/state/snapshots
 * Paginated timeline of asset state snapshots for the node.
 */
export async function listAssetStateSnapshots(
  siteId: string,
  nodeId: string,
  page = 1,
  limit = 20,
): Promise<PaginatedSnapshots<AssetStateSnapshot>> {
  return apiFetch<PaginatedSnapshots<AssetStateSnapshot>>(
    `/api/v2/sites/${siteId}/asset-nodes/${nodeId}/state/snapshots?page=${page}&limit=${limit}`,
  );
}

/**
 * GET /api/v2/sites/:siteId/asset-nodes/:nodeId/state/delta
 * Structured delta between two asset snapshots for the node.
 */
export async function getAssetStateDelta(
  siteId: string,
  nodeId: string,
  fromId: string,
  toId?: string,
): Promise<AssetStateDelta> {
  const qs = toId
    ? `from=${encodeURIComponent(fromId)}&to=${encodeURIComponent(toId)}`
    : `from=${encodeURIComponent(fromId)}`;
  return apiFetch<AssetStateDelta>(
    `/api/v2/sites/${siteId}/asset-nodes/${nodeId}/state/delta?${qs}`,
  );
}

// ─── Recompute endpoint (C2.3) ────────────────────────────────────────────────

/**
 * POST /api/v2/sites/:siteId/state/recompute
 * Trigger deterministic state snapshot recomputation.
 *
 * scope='all' (default): recompute all assets then site.
 * scope='site': recompute site aggregate only.
 * scope='assets': recompute specified or all assets, skip site.
 */
export async function triggerRecompute(
  siteId: string,
  scope: 'all' | 'assets' | 'site' = 'all',
  assetIds?: string[],
): Promise<RecomputeResult> {
  return apiFetch<RecomputeResult>(
    `/api/v2/sites/${siteId}/state/recompute`,
    {
      method: 'POST',
      body: JSON.stringify({
        scope,
        ...(assetIds ? { asset_ids: assetIds } : {}),
      }),
    },
    // Recompute fans out N per-asset D1 round-trips on the worker. Override the
    // 30 s apiFetch default but stay under Cloudflare's ~100 s edge timeout so
    // the client aborts first and surfaces "Request timed out" instead of 524.
    90_000,
  );
}
